import {useQuery} from "@apollo/client";
import {useEffect, useState} from "react";
import {DataGrid} from "@mui/x-data-grid";
import {AppBar, Box, Button, Toolbar, Tooltip, Typography, Checkbox} from "@mui/material";
import {NavLink as RouterLink, Outlet, useNavigate} from "react-router-dom";

import {ALL_CONTENT_INFOS} from "../queries/GraphQLQueries";
import OpenFile from "../components/OpenFile";
import Loader from "../components/Loader";
import {toTitleCase} from "../helpers/TextUtils";
import {getContentCapLevelName} from "../helpers/CapLevelHelper";

const SINGLE_LINE_ROW_HEIGHT_PIXELS = 40;

const styles = {
    box: {
        listViewContainer: {
            flexGrow: 1,
            padding: '0.25rem 1rem 0.5rem 1rem',
        },
    },
    actionButton: {
        marginLeft: 1,
    },
    dataGrid: {
        marginTop: '0.75rem',
        maxHeight: 'calc(100vh - 8.5rem)',
        '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
        },
        '& .MuiDataGrid-footerContainer': {
            minHeight: 40,
            maxHeight: 40,
        },
    },
};

function getContentFileUrl(params) {
    return (
            <OpenFile fileName={params.row.externalUrlName} fileUrl={params.row.externalUrl} />
    );
}

function RenderEditButton(params) {

    const navigate = useNavigate();

    function handleParams() {

        navigate('edit', {
            state: {
                contentId: params.row.id,
                contentDescription: params.row.description,
                contentName: params.row.name,
                contentNameExternal: params.row.nameExternal,
                contentCode: params.row.code,
                contentIsAssessed: params.row.isAssessed,
                contentExternalUrl: params.row.externalUrl,
                contentExternalUrlName: params.row.externalUrlName,
                currentServiceTypeKeycode: RenderServiceTypeKeycode(params),
                currentServiceType: RenderServiceType(params),
                contentLevel: getContentCapLevelName(params.row.level),
                currentContentType: params.row.contentType,
                isEditDialogBox: true
            }
        });
    }

    return (
        <Button variant="contained" size="small" sx={styles.actionButton} onClick={handleParams}>
            Edit
        </Button>
    )
}

function RenderDeleteButton(params) {

    const navigate = useNavigate();

    function handleDeleteButtonClick() {
        navigate('delete', {
            state: {
                contentId: params.row.id,
                contentName: params.row.name
            }
        });
    }

    return (
        <Button variant="contained" size="small" sx={styles.actionButton} onClick={handleDeleteButtonClick}>
            Delete
        </Button>
    )
}

function RenderBoolean(params) {
    return <Checkbox disabled checked={params.formattedValue} />;
}

function RenderServiceType(params) {
    return params.row.serviceType.description;
}

function RenderServiceTypeKeycode(params) {
    return params.row.serviceType.keycode;
}

function RenderContentType(params) {
    return toTitleCase(params.row.contentType);
}

const columns = [
    { field: 'id', headerName: 'Id', width: 50, editable: false },
    {
        field: 'name', headerName: 'Name', width: 120, editable: false,
        renderCell: (params) => {
            return <Tooltip title={params.formattedValue}>
                <Typography className="text-ellipsis" variant="body2">{params.formattedValue}</Typography>
            </Tooltip>
        },
    },
    {
        field: 'nameExternal', headerName: 'External Name', width: 120, editable: false,
        renderCell: (params) => {
            return <Tooltip title={params.formattedValue}>
                <Typography className="text-ellipsis" variant="body2">{params.formattedValue}</Typography>
            </Tooltip>
        },
    },
    {
        field: 'code', headerName: 'Code', width: 100, editable: false,
        renderCell: (params) => {
            return <Tooltip title={params.formattedValue}>
                <Typography className="text-ellipsis" variant="body2">{params.formattedValue}</Typography>
            </Tooltip>
        },
    },
    {
        field: 'description', headerName: 'Description', width: 300, editable: false,
        renderCell: (params) => {
            return <Tooltip title={params.formattedValue}>
                <Typography className="text-ellipsis" variant="body2">{params.formattedValue}</Typography>
            </Tooltip>
        },
    },
    { field: 'serviceType', headerName: 'Service Type', width: 125, editable: false, renderCell: RenderServiceType },
    { field: 'contentType', headerName: 'Content Type', width: 125, editable: false, renderCell: RenderContentType },
    { field: 'level', headerName: 'CAP Level', width: 90, editable: false, renderCell: (params) => getContentCapLevelName(params.row.level) },
    { field: 'isAssessed', headerName: 'Assessed', width: 100, editable: false, align: 'center', renderCell: RenderBoolean },
    { field: 'externalUrl', renderCell: getContentFileUrl, headerName: 'Content File', width: 100, editable: false },
    { field: 'created', headerName: 'Created', width: 200, type: 'datetime', hide: true },
    { field: 'editRow', renderCell: RenderEditButton, headerName: 'Edit', width: 100, editable: false },
    { field: 'deleteRow', renderCell: RenderDeleteButton, headerName: 'Delete', width: 100, editable: false },
];

export default function ContentInfoListView(props) {
    const { loading, error, data } = useQuery(ALL_CONTENT_INFOS);
    const [content, setContent] = useState([]);

    useEffect(() => {
         if (data && data.contentInfos) {
            setContent(data.contentInfos);
        }
    }, [data]);

    if (loading) {
        return <Loader />;
    }

    return (

        <Box sx={styles.box.listViewContainer}>
            <Button size="small" variant="contained" to="new" component={RouterLink}>Create</Button>
            <DataGrid
                className={'mui-table'}
                sx={styles.dataGrid}
                rows={content}
                columns={columns}
                getRowHeight={()=> SINGLE_LINE_ROW_HEIGHT_PIXELS}/>
            <Outlet />
        </Box>);
}
